// src/hooks/useMyClasses.ts
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface MyYearGroup {
  id: string;
  name: string;
  keyStageId: string;
  keyStageName: string;
}

export interface MyClass {
  id: string;
  name: string;
  yearGroupId: string;
  yearGroupName: string;
}

export interface MyClassesData {
  yearGroups: MyYearGroup[];
  classes: MyClass[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Fixed list used to derive key stage and filter available year groups
export const ALL_YEAR_GROUPS = [
  'Reception', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6',
] as const;

export function deriveKSName(yearGroup: string): 'KS1' | 'KS2' {
  return ['Reception', 'Year 1', 'Year 2'].includes(yearGroup) ? 'KS1' : 'KS2';
}

export function useMyClasses(): MyClassesData {
  const { user, profile } = useAuth();

  const [yearGroups, setYearGroups] = useState<MyYearGroup[]>([]);
  const [classes, setClasses] = useState<MyClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!user || !profile?.school_id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      // Fetch key_stages + year_groups for this school
      const { data: ksData, error: ksError } = await supabase
        .from('key_stages')
        .select('id, name, year_groups(id, name)')
        .eq('school_id', profile.school_id);
      if (ksError) throw new Error(ksError.message);

      const ygs: MyYearGroup[] = [];
      for (const ks of ksData ?? []) {
        for (const yg of (ks.year_groups as { id: string; name: string }[] ?? [])) {
          ygs.push({ id: yg.id, name: yg.name, keyStageId: ks.id, keyStageName: ks.name });
        }
      }
      setYearGroups(ygs);

      // Fetch teacher's classes via teacher_classes
      const { data: tcData, error: tcError } = await supabase
        .from('teacher_classes')
        .select('class_id, classes(id, name, year_group_id, year_groups(id, name))')
        .eq('user_id', user.id);
      if (tcError) throw new Error(tcError.message);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cls: MyClass[] = ((tcData ?? []) as any[])
        .map((row) => {
          const c = row.classes;
          return {
            id: c?.id ?? '',
            name: c?.name ?? '',
            yearGroupId: c?.year_group_id ?? '',
            yearGroupName: c?.year_groups?.name ?? '',
          };
        })
        .filter((c) => c.id);

      setClasses(cls);
    } catch (err) {
      setError((err as Error).message ?? 'Failed to load classes');
    } finally {
      setLoading(false);
    }
  }, [user, profile?.school_id]);

  useEffect(() => { loadData(); }, [loadData]);

  return { yearGroups, classes, loading, error, refetch: loadData };
}
